import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import ReactSelectBase from 'react-select';

import { zodResolver } from '@hookform/resolvers/zod';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import InputBase from '@material-ui/core/InputBase';
import InputLabel from '@material-ui/core/InputLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CreatableSelect from 'react-select/creatable';

import Paper from '@mui/material/Paper';

import { Toaster, toast } from 'react-hot-toast';
import employeeService from '../../../services/EmployeeService';
import taskService from '../../../services/TaskService';
import {
  TASK_TYPES,
  TASK_STATUSES,
  TASK_PRIORITIES,
  TASK_CRITICAL_REASONS,
  TASK_PENDING_REASONS,
  mapTaskToRow
} from '../helper/helper';
import { taskSchema } from '../schema/taskSchema';
import {
  createExternalSupportingStaffOption,
  employeeSupportingStaffOption,
  externalSupportingStaffOption,
  isValidSupportingStaffEmail,
  splitSupportingStaffOptions
} from '../helper/supportingStaff';

import Draggable from 'react-draggable';

const DraggablePaper = (props) => {
  const nodeRef = useRef(null);

  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#add-task-dialog-title"
      cancel={
        '[class*="MuiDialogContent-root"], [class*="MuiDialogActions-root"], input, textarea, button, select, option'
      }
      bounds="parent"
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
};

// ---------------------------------------------------------------------------
// Styled input – reused from existing pattern
// ---------------------------------------------------------------------------
const BootstrapInput = withStyles((theme) => ({
  root: { 'label + &': {} },
  input: {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.common.white,
    border: '2px solid #ced4da',
    fontSize: 16,
    width: '100%',
    padding: '10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    fontFamily: ['Roboto', 'Arial', 'sans-serif'].join(','),
    '&:focus': { borderColor: theme.palette.primary.main }
  }
}))(InputBase);

const useStyles = makeStyles((theme) => ({
  inputLabel: { color: 'rgb(107, 108, 114)', fontSize: '1rem' },
  bootstrapInput: { maxWidth: '100%', width: '100%' },
  paper: { padding: theme.spacing(2), color: theme.palette.text.secondary },
  sectionTitle: { fontWeight: 600 },
  errorText: {
    color: 'red',
    marginTop: '6px',
    fontSize: '0.8rem',
    lineHeight: 1,
    marginBottom: 0
  },
  fieldWrapper: { marginBottom: theme.spacing(1) },
  attachmentPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 4,
    backgroundColor: theme.palette.grey[50]
  },
  attachmentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  attachmentList: {
    display: 'grid',
    gap: theme.spacing(0.5)
  },
  attachmentRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    minWidth: 0,
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    backgroundColor: theme.palette.common.white
  },
  attachmentName: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
}));

// ---------------------------------------------------------------------------
// Select styles matching the app theme
// ---------------------------------------------------------------------------
const reactSelectStyles = {
  control: (base) => ({ ...base, minHeight: '42px', borderColor: '#ced4da', borderWidth: 2 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menuList: (base) => ({
    ...base,
    maxHeight: 240,
    overflowY: 'auto',
    overscrollBehavior: 'contain'
  })
};

const ReactSelect = (props) => (
  <ReactSelectBase
    {...props}
    isSearchable
    maxMenuHeight={240}
    menuPlacement="auto"
    menuPosition="fixed"
    menuShouldScrollIntoView={false}
  />
);

// ---------------------------------------------------------------------------
// Helper to convert an array of strings to ReactSelect options
// ---------------------------------------------------------------------------
const toOptions = (arr) => arr.map((v) => ({ label: v, value: v }));

const getDefaultDueDate = () => {
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + 7);
  return [
    dueDate.getFullYear(),
    String(dueDate.getMonth() + 1).padStart(2, '0'),
    String(dueDate.getDate()).padStart(2, '0')
  ].join('-');
};

const getDefaultTaskValues = () => ({
  title: '',
  type: 'Site Item',
  priority: 'Normal',
  status: 'Draft',
  dueDate: getDefaultDueDate(),
  projectName: null,
  class: null,
  location: '',
  description: '',
  estimatedHours: '',
  assignedTo: null,
  supportingStaff: [],
  dependencies: [],
  criticalReason: '',
  pendingReason: '',
  pendingComment: '',
  startTime: ''
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const AddTaskDialog = ({
  open,
  setOpen,
  setRows,
  onTaskSaved,
  classRecords,
  customerRecords,
  dependencyOptions,
  taskSource
}) => {
  const classes = useStyles();

  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [externalContactOptions, setExternalContactOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const classOptions = classRecords.map((c) => {
    return { value: c.value, label: c.name, id: c.value };
  });

  const customerOptions = customerRecords.map((c) => {
    return { value: c.value, label: c.name, id: c.value };
  });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(taskSchema),
    defaultValues: getDefaultTaskValues(),
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const watchedPriority = watch('priority');
  const watchedStatus = watch('status');

  // -------------------------------------------------------------------------
  // Load employees
  // -------------------------------------------------------------------------
  useEffect(() => {
    const load = async () => {
      // eslint-disable-next-line no-undef
      const [employeesResult, contactsResult] = await Promise.allSettled([
        employeeService.getActiveEmployees(),
        taskService.getExternalContacts()
      ]);
      if (employeesResult.status === 'fulfilled') {
        const records = employeesResult.value?.data?.data || [];
        setEmployeeOptions(
          records.map((e) => ({
            value: e._id || e.Id,
            label: e.DisplayName || `${e.FirstName} ${e.LastName}`
          }))
        );
      }
      if (contactsResult.status === 'fulfilled') {
        setExternalContactOptions((contactsResult.value?.data?.data || []).map(externalSupportingStaffOption));
      }
    };
    load();
  }, []);

  const statusOptions = useMemo(
    () => toOptions(TASK_STATUSES.filter((status) => ['Draft', 'Assigned'].includes(status))),
    []
  );

  const supportingStaffOptions = useMemo(
    () => [...employeeOptions.map(employeeSupportingStaffOption), ...externalContactOptions],
    [employeeOptions, externalContactOptions]
  );

  const updateStatus = (status) => {
    setValue('status', status, { shouldDirty: true, shouldValidate: true });

    if (status === 'Draft') {
      setValue('assignedTo', null, { shouldDirty: true, shouldValidate: true });
    }
  };

  const updateAssignee = (assignee) => {
    setValue('status', assignee ? 'Assigned' : 'Draft', {
      shouldDirty: true,
      shouldValidate: true
    });
    setValue('assignedTo', assignee, { shouldDirty: true, shouldValidate: true });
  };

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const staffRecipients = splitSupportingStaffOptions(data.supportingStaff || []);
      // Build the payload matching the task model shape
      const taskObj = {
        title: data.title,
        type: data.type,
        priority: data.priority,
        status: data.status,
        dueDate: data.dueDate || undefined,
        project: data.projectName || undefined,
        class: data.class || undefined,
        location: data.location || undefined,
        description: data.description || '',
        estimatedHours: data.estimatedHours ? Number(data.estimatedHours) : undefined,
        assignedTo: data.assignedTo ? [{ employeeId: data.assignedTo.value, assignedDate: new Date() }] : [],
        supportingStaff: staffRecipients.supportingStaff,
        externalRecipients: staffRecipients.externalRecipients,
        dependencies: (data.dependencies || []).map((dependency) => dependency.value),
        workflow: {
          criticalReason: data.priority === 'Critical' ? data.criticalReason : undefined,
          pendingReason: data.status === 'Pending' ? data.pendingReason : undefined,
          pendingComment: data.status === 'Pending' ? data.pendingComment : undefined
        },
        startTime: data.startTime || undefined
      };

      const response = await taskService.createTask(taskObj, taskSource);
      const newTask = response?.data?.data;
      let savedTask = newTask;
      let attachmentUploadFailed = false;

      if (newTask && selectedFiles.length) {
        try {
          const uploadResponse = await taskService.addAttachments(newTask._id || newTask.id, selectedFiles);
          savedTask = uploadResponse?.data?.data || newTask;
        } catch (attachmentError) {
          attachmentUploadFailed = true;
          toast.error(
            attachmentError?.response?.data?.message || 'Task was created, but its attachments could not be uploaded'
          );
        }
      }

      if (savedTask) {
        if (onTaskSaved) await onTaskSaved(savedTask);
        else setRows((prev) => [mapTaskToRow(savedTask), ...prev]);
      }

      if (!attachmentUploadFailed) {
        toast.success(selectedFiles.length ? 'Task and attachments created successfully' : 'Task created successfully');
      }
      reset(getDefaultTaskValues());
      setSelectedFiles([]);
      setOpen(false);
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || 'Could not create task';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset(getDefaultTaskValues());
    setSelectedFiles([]);
    setOpen(false);
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------
  const renderError = (fieldName) =>
    errors[fieldName] ? <p className={classes.errorText}>{errors[fieldName]?.message}</p> : null;

  const renderLabel = (label, required) => (
    <InputLabel shrink className={classes.inputLabel}>
      {label}
      {required && <span style={{ color: 'red' }}> *</span>}
    </InputLabel>
  );

  //handling files/attachment
  const handleFileSelection = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 10) {
      toast.error('Choose no more than 10 files');
      event.target.value = '';
      return;
    }

    const oversizedFile = files.find((file) => file.size > 5 * 1024 * 1024);
    if (oversizedFile) {
      toast.error(`${oversizedFile.name} is larger than 5 MB`);
      event.target.value = '';
      return;
    }
    setSelectedFiles(files);
    event.target.value = '';
  };

  const handleRemoveFile = (fileIndex) => {
    setSelectedFiles((files) => files.filter((_, index) => index !== fileIndex));
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="add-task-dialog-title"
        fullWidth
        maxWidth="md"
        PaperComponent={DraggablePaper}
      >
        <Toaster
          position="bottom-center"
          toastOptions={{
            duration: 3000,
            success: { style: { background: '#36c95e', color: '#fff' } },
            error: { style: { background: '#f5251d', color: '#fff' } }
          }}
        />
        <DialogTitle
          id="add-task-dialog-title"
          sx={{ px: { xs: 2, sm: 3.5 }, pt: 2.5, pb: 1, cursor: 'move', userSelect: 'none' }}
        >
          Create Task
        </DialogTitle>
        <DialogContent>
          <form id="add-task-form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              {/* ──────────────────────────────────────────── */}
              {/* SECTION: Core Details                        */}
              {/* ──────────────────────────────────────────── */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" className={classes.sectionTitle}>
                  Core Details
                </Typography>
              </Grid>

              {/* Title */}
              <Grid item xs={12} sm={8}>
                <div className={classes.fieldWrapper}>
                  {renderLabel('Task Title', true)}
                  <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                      <BootstrapInput
                        {...field}
                        onMouseDown={(e) => e.stopPropagation()}
                        className={classes.bootstrapInput}
                        placeholder="Enter task title"
                      />
                    )}
                  />
                  {renderError('title')}
                </div>
              </Grid>

              {/* Type */}
              <Grid item xs={12} sm={4}>
                <div className={classes.fieldWrapper}>
                  {renderLabel('Task Type', true)}
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <ReactSelect
                        inputId="task-type"
                        options={toOptions(TASK_TYPES)}
                        value={field.value ? { label: field.value, value: field.value } : null}
                        onChange={(opt) => field.onChange(opt?.value || '')}
                        placeholder="Select type..."
                        styles={reactSelectStyles}
                        // eslint-disable-next-line no-undef
                        menuPortalTarget={document.body}
                      />
                    )}
                  />
                  {renderError('type')}
                </div>
              </Grid>

              {/* Project */}
              <Grid item xs={12} sm={4}>
                <div className={classes.fieldWrapper}>
                  {renderLabel('Project / Internal Area', true)}
                  <Controller
                    name="projectName"
                    control={control}
                    render={({ field }) => (
                      <ReactSelect
                        inputId="projectName"
                        options={customerOptions}
                        value={
                          field.value
                            ? {
                                label: field.value.name,
                                value: field.value.id
                              }
                            : null
                        }
                        onChange={(opt) =>
                          field.onChange(
                            opt
                              ? {
                                  id: opt.id,
                                  name: opt.label
                                }
                              : null
                          )
                        }
                        placeholder="Select project"
                        styles={reactSelectStyles}
                        // eslint-disable-next-line no-undef
                        menuPortalTarget={document.body}
                      />
                    )}
                  />
                  {renderError('projectName')}
                </div>
              </Grid>

              {/* Class */}
              <Grid item xs={12} sm={4}>
                <div className={classes.fieldWrapper}>
                  {renderLabel('Class')}
                  <Controller
                    name="class"
                    control={control}
                    render={({ field }) => (
                      <ReactSelect
                        inputId="class"
                        options={classOptions}
                        value={
                          field.value
                            ? {
                                label: field.value.name,
                                value: field.value.id
                              }
                            : null
                        }
                        onChange={(opt) =>
                          field.onChange(
                            opt
                              ? {
                                  id: opt.id,
                                  name: opt.label
                                }
                              : null
                          )
                        }
                        placeholder="Select class"
                        isClearable
                        styles={reactSelectStyles}
                        // eslint-disable-next-line no-undef
                        menuPortalTarget={document.body}
                      />
                    )}
                  />
                  {renderError('class')}
                </div>
              </Grid>

              {/* Location */}
              <Grid item xs={12} sm={4}>
                <div className={classes.fieldWrapper}>
                  {renderLabel('Location / Area')}
                  <Controller
                    name="location"
                    control={control}
                    render={({ field }) => (
                      <BootstrapInput
                        {...field}
                        className={classes.bootstrapInput}
                        placeholder="e.g. Level 2 – Room 14"
                      />
                    )}
                  />
                </div>
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <div className={classes.fieldWrapper}>
                  {renderLabel('Description', true)}
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <BootstrapInput
                        {...field}
                        className={classes.bootstrapInput}
                        multiline
                        rows={3}
                        placeholder="Describe what needs to be done..."
                        inputProps={{ style: { height: 'auto' } }}
                      />
                    )}
                  />
                  {renderError('description')}
                </div>
              </Grid>

              {/* ──────────────────────────────────────────── */}
              {/* SECTION: Priority & Status                   */}
              {/* ──────────────────────────────────────────── */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" className={classes.sectionTitle}>
                  Priority &amp; Status
                </Typography>
              </Grid>

              {/* Priority */}
              <Grid item xs={12} sm={4}>
                <div className={classes.fieldWrapper}>
                  {renderLabel('Priority', true)}
                  <Controller
                    name="priority"
                    control={control}
                    render={({ field }) => (
                      <ReactSelect
                        inputId="priority"
                        options={toOptions(TASK_PRIORITIES)}
                        value={field.value ? { label: field.value, value: field.value } : null}
                        onChange={(opt) => field.onChange(opt?.value || '')}
                        placeholder="Select priority..."
                        styles={reactSelectStyles}
                        // eslint-disable-next-line no-undef
                        menuPortalTarget={document.body}
                      />
                    )}
                  />
                  {renderError('priority')}
                </div>
              </Grid>

              {/* Critical Reason – only shown when Critical selected */}
              {watchedPriority === 'Critical' && (
                <Grid item xs={12} sm={8}>
                  <div className={classes.fieldWrapper}>
                    {renderLabel('Critical Reason', true)}
                    <Controller
                      name="criticalReason"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          inputId="critical-reason"
                          options={toOptions(TASK_CRITICAL_REASONS)}
                          value={field.value ? { label: field.value, value: field.value } : null}
                          onChange={(opt) => field.onChange(opt?.value || '')}
                          placeholder="Select reason for critical priority..."
                          styles={reactSelectStyles}
                          // eslint-disable-next-line no-undef
                          menuPortalTarget={document.body}
                        />
                      )}
                    />
                  </div>
                </Grid>
              )}

              {/* Status */}
              <Grid item xs={12} sm={4}>
                <div className={classes.fieldWrapper}>
                  {renderLabel('Status', true)}
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <ReactSelect
                        inputId="status"
                        options={statusOptions}
                        value={field.value ? { label: field.value, value: field.value } : null}
                        onChange={(opt) => updateStatus(opt?.value || '')}
                        placeholder="Select status..."
                        styles={reactSelectStyles}
                        // eslint-disable-next-line no-undef
                        menuPortalTarget={document.body}
                      />
                    )}
                  />
                  {renderError('status')}
                </div>
              </Grid>

              {/* Dependencies */}
              <Grid item xs={12} sm={4}>
                <div className={classes.fieldWrapper}>
                  {renderLabel('Dependencies')}
                  <Controller
                    name="dependencies"
                    control={control}
                    render={({ field }) => (
                      <ReactSelect
                        isMulti
                        isClearable
                        inputId="dependencies"
                        options={dependencyOptions}
                        value={field.value || []}
                        onChange={(options) => field.onChange(options || [])}
                        placeholder="Select task Dependencies..."
                        styles={reactSelectStyles}
                        // eslint-disable-next-line no-undef
                        menuPortalTarget={document.body}
                      />
                    )}
                  />
                  {renderError('dependencies')}
                </div>
              </Grid>

              {/* Pending fields – only shown when Pending selected */}
              {watchedStatus === 'Pending' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <div className={classes.fieldWrapper}>
                      {renderLabel('Pending Reason', true)}
                      <Controller
                        name="pendingReason"
                        control={control}
                        render={({ field }) => (
                          <ReactSelect
                            inputId="pending-reason"
                            options={toOptions(TASK_PENDING_REASONS)}
                            value={field.value ? { label: field.value, value: field.value } : null}
                            onChange={(opt) => field.onChange(opt?.value || '')}
                            placeholder="Why is this pending?"
                            styles={reactSelectStyles}
                            // eslint-disable-next-line no-undef
                            menuPortalTarget={document.body}
                          />
                        )}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <div className={classes.fieldWrapper}>
                      {renderLabel('Pending Comment', true)}
                      <Controller
                        name="pendingComment"
                        control={control}
                        render={({ field }) => (
                          <BootstrapInput
                            {...field}
                            className={classes.bootstrapInput}
                            multiline
                            rows={2}
                            placeholder="Explain what is outstanding..."
                          />
                        )}
                      />
                    </div>
                  </Grid>
                </>
              )}

              {/* ──────────────────────────────────────────── */}
              {/* SECTION: Assignment & Schedule               */}
              {/* ──────────────────────────────────────────── */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" className={classes.sectionTitle}>
                  Assignment &amp; Schedule
                </Typography>
              </Grid>

              {/* Assigned To */}
              <Grid item xs={12} sm={6}>
                <div className={classes.fieldWrapper}>
                  {renderLabel('Assign To')}
                  <Controller
                    name="assignedTo"
                    control={control}
                    render={({ field }) => (
                      <ReactSelect
                        inputId="assigned-to"
                        options={employeeOptions}
                        value={field.value}
                        onChange={updateAssignee}
                        placeholder="Select employee..."
                        isClearable
                        styles={reactSelectStyles}
                        // eslint-disable-next-line no-undef
                        menuPortalTarget={document.body}
                      />
                    )}
                  />
                  {renderError('assignedTo')}
                </div>
              </Grid>

              {/* Supporting Staff */}
              <Grid item xs={12} sm={6}>
                <div className={classes.fieldWrapper}>
                  {renderLabel('Supporting Staff')}
                  <Controller
                    name="supportingStaff"
                    control={control}
                    render={({ field }) => (
                      <CreatableSelect
                        inputId="supporting-staff"
                        options={supportingStaffOptions}
                        value={field.value}
                        onChange={(opts) => field.onChange(opts || [])}
                        onCreateOption={(inputValue) => {
                          if (!isValidSupportingStaffEmail(inputValue)) {
                            toast.error('Enter a valid email address');
                            return;
                          }
                          const newOption = createExternalSupportingStaffOption(inputValue);
                          const alreadySelected = (field.value || []).some(
                            (option) => option.value === newOption.value
                          );
                          if (!alreadySelected) field.onChange([...(field.value || []), newOption]);
                        }}
                        placeholder="Select supporting staff or type custom email"
                        isMulti
                        isClearable
                        styles={reactSelectStyles}
                        // eslint-disable-next-line no-undef
                        menuPortalTarget={document.body}
                      />
                    )}
                  />
                </div>
              </Grid>

              {/* Due Date */}
              <Grid item xs={12} sm={4}>
                <div className={classes.fieldWrapper}>
                  {renderLabel('Due Date', true)}
                  <Controller
                    name="dueDate"
                    control={control}
                    render={({ field }) => <BootstrapInput {...field} className={classes.bootstrapInput} type="date" />}
                  />
                  {renderError('dueDate')}
                </div>
              </Grid>

              {/* Estimated Hours */}
              <Grid item xs={12} sm={4}>
                <div className={classes.fieldWrapper}>
                  {renderLabel('Estimated Hours')}
                  <Controller
                    name="estimatedHours"
                    control={control}
                    render={({ field }) => (
                      <BootstrapInput
                        {...field}
                        className={classes.bootstrapInput}
                        type="number"
                        inputProps={{ min: 0, step: 0.5 }}
                      />
                    )}
                  />
                </div>
              </Grid>

              {/* Start Time */}
              <Grid item xs={12} sm={4}>
                <div className={classes.fieldWrapper}>
                  {renderLabel('Start Time')}
                  <Controller
                    name="startTime"
                    control={control}
                    render={({ field }) => (
                      <BootstrapInput
                        {...field}
                        className={classes.bootstrapInput}
                        type="time"
                        slotProps={{ field: { clearable: true } }}
                      />
                    )}
                  />
                </div>
              </Grid>

              {/* ──────────────────────────────────────────── */}
              {/* SECTION: Photos & Attachments                */}
              {/* ──────────────────────────────────────────── */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" className={classes.sectionTitle}>
                  Photos &amp; Attachments
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <div className={`${classes.fieldWrapper} ${classes.attachmentPanel}`}>
                  <div className={classes.attachmentHeader}>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadFileOutlinedIcon />}
                      disabled={isSubmitting}
                      sx={{ textTransform: 'none' }}
                    >
                      {selectedFiles.length ? 'Replace files' : 'Choose files'}
                      <input
                        hidden
                        multiple
                        type="file"
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                        onChange={handleFileSelection}
                        name="attachments"
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Up to 10 files, 5 MB each. Images, PDF, Word, Excel, CSV, and text files are supported.
                    </Typography>
                  </div>

                  {selectedFiles.length > 0 && (
                    <Box className={classes.attachmentList}>
                      {selectedFiles.map((file, index) => (
                        <div className={classes.attachmentRow} key={`${file.name}-${file.lastModified}-${index}`}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            className={classes.attachmentName}
                            title={file.name}
                          >
                            {file.name}
                          </Typography>
                          <IconButton
                            size="small"
                            aria-label={`Remove ${file.name}`}
                            onClick={() => handleRemoveFile(index)}
                            disabled={isSubmitting}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </div>
                      ))}
                    </Box>
                  )}
                </div>
              </Grid>
            </Grid>
          </form>
        </DialogContent>

        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={handleClose} color="secondary" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" form="add-task-form" variant="contained" color="primary" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <CircularProgress size={16} style={{ color: 'white', marginRight: 8 }} />
                Saving...
              </>
            ) : (
              'Create Task'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AddTaskDialog;

AddTaskDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  setRows: PropTypes.func.isRequired,
  onTaskSaved: PropTypes.func,
  classRecords: PropTypes.array.isRequired,
  customerRecords: PropTypes.array.isRequired,
  dependencyOptions: PropTypes.array.isRequired,
  taskSource: PropTypes.oneOf(['myTask', 'totalTask']).isRequired
};
